import { MouseEvent, useRef, useEffect, useState, SetStateAction, Dispatch } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import classNames from 'classnames';
import { Constants } from '@praktio/common';

import { findBaseResource } from '../../utils/slide';
import { loadImageDimensions } from '../../utils/image';
import TextLoader from '../SlideAssets/TextLoader';

// Ducks
import { RootState } from '../../ducks/configureStore';
import { update as updateQuiz } from '../../ducks/quizLitmosReducer';

// Styles
import './InteractiveDocument.scss';
import SupplementalDocument from '../InstructionalPanel/SupplementalDocument/SupplementalDocument';
import { ImageDisplayMode, SupplementalDocStates, WindowSizes } from '../../utils/constants';

const {
  ResourceItemTypes,
  CataTypes,
  AnnotationTypes,
  TemplateTypes,
  ResourceTypes,
  ImageBackgroundModes,
} = Constants;

interface IProps {
  panelIds?: string[];
  layoutIsHorizontal: boolean;
  hasLandscapeDoc: boolean;
  hasPortraitDoc: boolean;
  newlyExpandedExplanation?: boolean;
  windowSize: WindowSizes;
  supplementalDoc?: SupplementalDoc;
  supplementalDocState?: SupplementalDocStates;
  setSupplementalDocState?: Dispatch<SetStateAction<SupplementalDocStates>>;
  setNewlyExpandedExplanation?: Dispatch<SetStateAction<boolean>>;
  choices?: Annotation[];
  displayedEditId?: string;
  onComputedMaxWidth?: (maxWidth: number | undefined) => void;
}

const InteractiveDocument = ({
  panelIds,
  layoutIsHorizontal,
  hasLandscapeDoc,
  hasPortraitDoc,
  newlyExpandedExplanation,
  windowSize,
  supplementalDoc,
  supplementalDocState,
  setNewlyExpandedExplanation,
  setSupplementalDocState,
  choices,
  displayedEditId,
  onComputedMaxWidth,
}: IProps) => {
  const dispatch = useDispatch();
  // REDUX STATE
  const {
    outOfTries,
    slideType,
    hideExcerptLabel,
    interactiveDocument,
    activityComplete,
    slideSubType,
    choicesShouldPulse,
    filteredDocumentTimeline,
    currentSlide,
    documentPanelId,
    tooltipEngaged,
    learnerId,
    isSubmitting,
  } = useSelector((state: RootState) => {
    const { slidesLitmos, quizLitmos, learner } = state;
    const { learnerId, isSubmitting } = learner;
    const { currentSlide } = slidesLitmos;
    const {
      outOfTries,
      completed: activityComplete,
      choicesShouldPulse,
      tooltipEngaged,
    } = quizLitmos;
    const {
      timeline,
      type: slideType,
      subType: slideSubType,
      hideExcerptLabel,
    } = currentSlide || {};

    let indexOfLastPanelId = (panelIds?.length || 0) - 1;
    if (indexOfLastPanelId < 0) {
      indexOfLastPanelId = 0;
    }

    const documentPanelId = panelIds?.[indexOfLastPanelId];

    const filteredDocumentTimeline = timeline?.filter(
      (resource) => resource.panelId === documentPanelId,
    );

    const arrayOfDocuments = filteredDocumentTimeline?.filter(
      (timelineElement) =>
        timelineElement.type === ResourceItemTypes.Document ||
        timelineElement.type === ResourceItemTypes.Text ||
        timelineElement.type === ResourceTypes.Image,
    );

    const interactiveDocument =
      arrayOfDocuments && arrayOfDocuments.length > 0 ? arrayOfDocuments[0] : undefined;

    return {
      hideExcerptLabel,
      outOfTries,
      slideType,
      interactiveDocument,
      activityComplete,
      slideSubType,
      choicesShouldPulse,
      filteredDocumentTimeline,
      currentSlide,
      documentPanelId,
      tooltipEngaged,
      learnerId,
      isSubmitting,
    };
  });

  // LOCAL STATE
  const prevTooltipEngaged = useRef(tooltipEngaged);
  const [maxAspectRatio, setMaxAspectRatio] = useState<number | null>(null);
  const [containerHeight, setContainerHeight] = useState(0);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Turn off purple pulsing if the tooltip was engaged but user has taken
  // action to disengage.
  useEffect(() => {
    if (prevTooltipEngaged.current && !tooltipEngaged) {
      dispatch(
        updateQuiz({
          choicesShouldPulse: false,
          explanationButtonsShouldPulse: false,
        }),
      );
    }

    prevTooltipEngaged.current = tooltipEngaged;
  }, [tooltipEngaged, dispatch]);

  // Determine which image to use based on CATA choice selection
  const getActiveImageData = () => {
    const defaultUrl = interactiveDocument?.url;
    const defaultSettings = interactiveDocument?.displaySettings;

    if (!displayedEditId || !choices?.length) {
      return { url: defaultUrl, settings: defaultSettings };
    }

    const selectedChoice = choices.find((c) => c._id === displayedEditId);
    const choiceImageUrl = selectedChoice?.imageUrl;

    // If showing a choice image, use choice's displayMode but keep panel's background settings
    if (choiceImageUrl && selectedChoice?.imageDisplaySettings) {
      const shouldInherit =
        !selectedChoice.imageDisplaySettings.displayMode ||
        selectedChoice.imageDisplaySettings.displayMode === 'inherit';

      return {
        url: choiceImageUrl,
        settings: {
          ...defaultSettings, // Keep panel's backgroundColor and backgroundMode
          displayMode: shouldInherit
            ? defaultSettings?.displayMode
            : selectedChoice.imageDisplaySettings.displayMode,
        },
      };
    }

    return {
      url: choiceImageUrl || defaultUrl,
      settings: defaultSettings,
    };
  };

  const activeImageData = getActiveImageData();
  const activeImageUrl = activeImageData.url;
  const activeDisplaySettings = activeImageData.settings;

  // Derive display mode and background mode from the active image's settings
  const displayMode = activeDisplaySettings?.displayMode || ImageDisplayMode.Contain;

  const backgroundMode = activeDisplaySettings?.backgroundMode || ImageBackgroundModes.Blur;

  const imageBackgroundColor =
    backgroundMode === ImageBackgroundModes.Black
      ? '#000'
      : backgroundMode === ImageBackgroundModes.DominantColor
      ? activeDisplaySettings?.backgroundColor || '#fff'
      : backgroundMode === ImageBackgroundModes.Blur
      ? 'transparent'
      : '#fff';

  const showBlurBackground = backgroundMode === ImageBackgroundModes.Blur;

  const isExcerpt = !!interactiveDocument?.snippedContent;

  const resource = filteredDocumentTimeline
    ? findBaseResource(filteredDocumentTimeline)
    : undefined;

  // Determine if the document is sharing width with anything (Choices, explanation panel)
  let docIsSharingWidth = false;
  if (
    slideSubType === CataTypes.EditVertical ||
    (slideSubType === CataTypes.EditHorizontal && activityComplete) ||
    slideSubType === CataTypes.Comment
  ) {
    docIsSharingWidth = true;
  }

  const updateCBSSelectionLiveRegion = (slideType: string, selectedId: string | number) => {
    if (slideType !== TemplateTypes.ClickBestSpot || !selectedId) {
      return;
    }

    const liveRegionEl = document.getElementById('CBS__LiveRegionSelection');

    if (!liveRegionEl) {
      return;
    }

    const selectedEl = document.querySelector(`[data-id="${selectedId}"]`);
    let selectedChar: string | undefined = selectedEl?.textContent?.replace(/[\u200C\u200B]/g, ''); // Remove zero-width space and zero-width non-breaking space characters (e.g. &#8203;T -> 'T')

    if (selectedChar === ' ') {
      selectedChar = 'Space';
    }

    if (!selectedChar) {
      return;
    }

    liveRegionEl.textContent = '';
    liveRegionEl.textContent = `Current Selection ${selectedChar}`;

    // TODO: Maybe break this out into a separate function
    // Remove tabindex and aria-label from any previously selected element
    // Make the selected element focusable
    const prevSelectedEl = document.querySelector('[data-id][tabindex]');

    prevSelectedEl?.removeAttribute('tabindex');

    selectedEl?.setAttribute('tabindex', '-1');
  };

  const onContentClick = (event: MouseEvent) => {
    const { timeline, paragraphSelection } = currentSlide || {};
    if (
      !outOfTries &&
      slideType === TemplateTypes.ClickBestSpot &&
      (windowSize === WindowSizes.Large ||
        supplementalDocState !== SupplementalDocStates.FullyOpen) &&
      !isSubmitting
    ) {
      let target = event.target as HTMLElement;
      const targetId = target?.id;
      // It's possible to select a child node inside of the correct
      // answer area, so we want to traverse up the tree
      // to make sure we verify the node or its ancestor has
      // the answer classes (up to a certain point of 'slide-panel',
      // anything beyond that point is considered incorrect)
      let selectedId;
      selectedId = -1;
      while (selectedId === -1 && target && target.className.indexOf('CBS') === -1) {
        let id;
        if (paragraphSelection && timeline !== undefined) {
          id = timeline.find((a) => a.subType === AnnotationTypes.Choice && a.rootId === targetId)
            ? targetId
            : null;
        } else {
          id = target.dataset.id;
        }

        if (id) {
          selectedId = id;
        } else if (target.parentNode) {
          target = target.parentNode as HTMLElement;
        }
      }

      if (
        selectedId !== -1 &&
        (!paragraphSelection || (paragraphSelection && selectedId && selectedId !== ''))
      ) {
        dispatch(
          updateQuiz({
            disableNext: true,
            showSubmit: true,
            selected: selectedId.toString(),
            tooltipEngaged: false,
          }),
        );
      }

      updateCBSSelectionLiveRegion(slideType, selectedId);
    }
  };

  // Check if any choice has an image (for min-height styling)
  const hasChoiceImages = choices?.some((c) => c.imageUrl);

  // Pre-load all slide images and find which one renders widest at contain-fit
  useEffect(() => {
    if (interactiveDocument?.type !== ResourceTypes.Image) {
      return;
    }

    const imageUrls: string[] = [];

    if (interactiveDocument.url) {
      imageUrls.push(interactiveDocument.url);
    }

    choices?.forEach((choice) => {
      if (choice.imageUrl) {
        imageUrls.push(choice.imageUrl);
      }
    });

    if (imageUrls.length === 0) {
      return;
    }

    Promise.all(imageUrls.map(loadImageDimensions)).then((results) => {
      const loaded = results.filter((r) => r.naturalWidth > 0 && r.naturalHeight > 0);

      const widest = loaded.reduce<(typeof loaded)[number] | null>((max, item) => {
        const itemAspect = item.naturalWidth / item.naturalHeight;
        const maxAspect = max ? max.naturalWidth / max.naturalHeight : 0;
        return itemAspect > maxAspect ? item : max;
      }, null);

      if (widest) {
        setMaxAspectRatio(widest.naturalWidth / widest.naturalHeight);
      }
    });
  }, [interactiveDocument?.type, interactiveDocument?.url, choices]);

  // Reset container height when the slide's image changes (e.g. navigating between slides)
  useEffect(() => {
    setContainerHeight(0);
  }, [interactiveDocument?.url]);

  // Track container height via ResizeObserver to compute maxWidth for choice images
  useEffect(() => {
    const el = wrapperRef.current;
    if (!hasChoiceImages || !hasLandscapeDoc || !el) return;

    let rafId: number;

    const observer = new ResizeObserver(([entry]) => {
      const newHeight = Math.round(entry.contentRect.height);
      rafId = requestAnimationFrame(() => {
        setContainerHeight((prev) => Math.max(prev, newHeight));
      });
    });

    observer.observe(el);

    return () => {
      observer.disconnect();
      cancelAnimationFrame(rafId);
    };
  }, [hasChoiceImages, hasLandscapeDoc]);

  // Constrain container width so the widest choice image fills it exactly at contain-fit
  const computedMaxWidth =
    hasLandscapeDoc && hasChoiceImages && maxAspectRatio && containerHeight
      ? Math.ceil(containerHeight * maxAspectRatio)
      : undefined;

  useEffect(() => {
    onComputedMaxWidth?.(computedMaxWidth);
  }, [computedMaxWidth, onComputedMaxWidth]);

  if (!!interactiveDocument) {
    return (
      <div
        ref={wrapperRef}
        className={classNames('InteractiveDocument-wrapper', {
          'has-supplemental-doc': supplementalDoc,
          'portrait-doc': hasPortraitDoc,
          'landscape-doc': hasLandscapeDoc,
        })}
        style={computedMaxWidth ? { maxWidth: computedMaxWidth } : undefined}
      >
        <div
          className={classNames('InteractiveDocument-container', {
            'horizontal-layout': layoutIsHorizontal,
            interactive: !activityComplete && slideType === TemplateTypes.ClickBestSpot,
            'image-resource': interactiveDocument?.type === ResourceTypes.Image,
            'has-choice-images': hasChoiceImages,
          })}
        >
          {isExcerpt && !hideExcerptLabel && <div className="excerpt-label">Excerpt</div>}
          {interactiveDocument?.type === ResourceTypes.Image ? (
            // TODO: Add dedicated altText field to resource schema for better ADA compliance
            <div
              className={classNames('InteractiveDocument-image', `display-${displayMode}`, {
                'background-blur': showBlurBackground,
              })}
              style={{
                backgroundImage: `url("${activeImageUrl}")`,
                backgroundColor: imageBackgroundColor,
              }}
              role="img"
              aria-label={(interactiveDocument as any).name || 'Image resource'}
            />
          ) : (
            <OverlayTrigger
              overlay={
                <Popover id="InteractiveDocument-popover">
                  <Popover.Content>DISABLED!</Popover.Content>
                </Popover>
              }
            >
              <TextLoader
                resource={resource}
                onSelect={onContentClick}
                shouldPulse={slideType === TemplateTypes.ClickBestSpot && choicesShouldPulse}
                sharedWidth={docIsSharingWidth}
                panelId={documentPanelId}
                newlyExpandedExplanation={newlyExpandedExplanation}
                setNewlyExpandedExplanation={setNewlyExpandedExplanation}
                windowSize={windowSize}
                hasSupplementalDoc={!!supplementalDoc}
                supplementalDocState={supplementalDocState}
              />
            </OverlayTrigger>
          )}
        </div>
        <SupplementalDocument
          supplementalDoc={supplementalDoc}
          learnerId={learnerId}
          supplementalDocState={supplementalDocState}
          setSupplementalDocState={setSupplementalDocState}
          embedded
          mainDocIsLandscape={hasLandscapeDoc}
        />
      </div>
    );
  }
  return null;
};

export default InteractiveDocument;
